import React from 'react';
import { Phone, MapPin, Clock, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { BUSINESS_INFO } from '@/lib/constants';

const ContactPage = () => {
    return (
        <div>
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-muted">
                <div className="container-custom py-16 md:py-20">
                    <div className="max-w-3xl">
                        <h1 className="text-4xl sm:text-5xl font-heading font-bold text-foreground mb-6">
                            İletişim
                        </h1>
                        <p className="text-lg text-muted-foreground leading-relaxed">
                            Klima hizmetlerimiz hakkında bilgi almak, randevu oluşturmak veya 
                            fiyat teklifi almak için bize ulaşın. Size en kısa sürede dönüş yapalım.
                        </p>
                    </div>
                </div>
            </section>

            {/* Contact Info Section */}
            <section className="section-padding">
                <div className="container-custom">
                    <div className="grid lg:grid-cols-2 gap-10">
                        {/* Contact Cards */}
                        <div className="space-y-6">
                            {/* Phone Card */}
                            <Card className="border-border/50">
                                <CardContent className="p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="shrink-0 w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                                            <Phone className="h-7 w-7 text-primary" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                                                Telefon
                                            </h3>
                                            <p className="text-muted-foreground mb-4">
                                                Hemen arayın, size yardımcı olalım
                                            </p>
                                            <a 
                                                href={`tel:${BUSINESS_INFO.phone}`}
                                                className="text-xl font-bold text-primary hover:underline"
                                            >
                                                {BUSINESS_INFO.phoneDisplay}
                                            </a>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* WhatsApp Card */}
                            <Card className="border-border/50">
                                <CardContent className="p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="shrink-0 w-14 h-14 rounded-xl bg-success/10 flex items-center justify-center">
                                            <svg className="h-7 w-7 text-success" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                            </svg>
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                                                WhatsApp
                                            </h3>
                                            <p className="text-muted-foreground mb-4">
                                                WhatsApp üzerinden mesaj gönderin
                                            </p>
                                            <Button variant="whatsapp" asChild>
                                                <a 
                                                    href={`https://wa.me/${BUSINESS_INFO.whatsapp}?text=Merhaba, klima hizmeti hakkında bilgi almak istiyorum.`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                                    </svg>
                                                    WhatsApp'tan Yazın
                                                </a>
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Address Card */}
                            <Card className="border-border/50">
                                <CardContent className="p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="shrink-0 w-14 h-14 rounded-xl bg-accent flex items-center justify-center">
                                            <MapPin className="h-7 w-7 text-accent-foreground" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-heading font-semibold text-lg text-foreground mb-2">
                                                Adres
                                            </h3>
                                            <p className="text-muted-foreground mb-4">
                                                {BUSINESS_INFO.address}
                                            </p>
                                            <Button variant="outline" asChild>
                                                <a 
                                                    href={BUSINESS_INFO.googleMapsUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <ExternalLink className="h-4 w-4" />
                                                    Google Haritalar'da Aç
                                                </a>
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Working Hours Card */}
                            <Card className="border-border/50">
                                <CardContent className="p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="shrink-0 w-14 h-14 rounded-xl bg-accent flex items-center justify-center">
                                            <Clock className="h-7 w-7 text-accent-foreground" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="font-heading font-semibold text-lg text-foreground mb-4">
                                                Çalışma Saatleri
                                            </h3>
                                            <div className="space-y-3">
                                                <div className="flex justify-between items-center py-2 border-b border-border">
                                                    <span className="text-muted-foreground">Pazartesi - Cumartesi</span>
                                                    <span className="font-medium text-foreground">{BUSINESS_INFO.workingHours.weekdays}</span>
                                                </div>
                                                <div className="flex justify-between items-center py-2">
                                                    <span className="text-muted-foreground">Pazar</span>
                                                    <span className="font-medium text-destructive">{BUSINESS_INFO.workingHours.sunday}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Map Section */}
                        <div className="lg:sticky lg:top-24">
                            <Card className="overflow-hidden border-border/50 h-full min-h-[500px]">
                                <div className="relative w-full h-full min-h-[500px] bg-muted">
                                    {/* Static Map Placeholder with actual info */}
                                    <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
                                        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-6">
                                            <MapPin className="h-10 w-10 text-primary" />
                                        </div>
                                        <h3 className="font-heading font-bold text-xl text-foreground mb-2">
                                            {BUSINESS_INFO.name}
                                        </h3>
                                        <p className="text-muted-foreground mb-6 max-w-sm">
                                            {BUSINESS_INFO.addressShort}
                                        </p>
                                        <p className="text-sm text-muted-foreground mb-6">
                                            Plus Code: {BUSINESS_INFO.plusCode}
                                        </p>
                                        <Button variant="default" size="lg" asChild>
                                            <a 
                                                href={BUSINESS_INFO.googleMapsUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <MapPin className="h-5 w-5" />
                                                Google Haritalar'da Aç
                                            </a>
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="section-padding bg-primary">
                <div className="container-custom text-center">
                    <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-4">
                        Hemen Bize Ulaşın
                    </h2>
                    <p className="text-primary-foreground/80 text-lg mb-8 max-w-2xl mx-auto">
                        Klima hizmetleriniz için en uygun çözümü birlikte bulalım. 
                        Ücretsiz keşif ve fiyat teklifi için arayın.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Button 
                            size="xl" 
                            className="bg-background text-primary hover:bg-background/90 font-semibold shadow-lg"
                            asChild
                        >
                            <a href={`tel:${BUSINESS_INFO.phone}`}>
                                <Phone className="h-5 w-5" />
                                {BUSINESS_INFO.phoneDisplay}
                            </a>
                        </Button>
                        <Button variant="whatsapp" size="xl" asChild>
                            <a 
                                href={`https://wa.me/${BUSINESS_INFO.whatsapp}?text=Merhaba, klima hizmeti hakkında bilgi almak istiyorum.`}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                </svg>
                                WhatsApp İle Yazın
                            </a>
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ContactPage;
